import { useState, useEffect, useMemo } from "react";
import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import Tag from "./Tag";
import CheckBox from "./CheckBox";
import ButtonIconH from "./ButtonIconH";
import RadioButton from "./RadioButton";
import { useAgents } from "../hooks/useAgents";
import { useGlassesTypes } from "../hooks/useGlassesTypes";
import { useRecordSuppliersAndBrands } from "../hooks/useRecordSuppliersAndBrands";
import {
  dashboardPatientContactLensesModule,
  dashboardPatientFFDates,
  dashboardPatientGlassesModule,
  dashboardPatientHearingAidsModule,
  dashboardPatientFFOptions,
  dashboardPatientFFToggle,
  contactLensesUseOptions,
  hearingAidsTypeOptions,
} from "../js/utils";
import { useCLTypes } from "../hooks/useCLTypes";

export function DashboardPatientPanelFileFilterSection({
  onChange,
  status = null,
  onClick = null,
  createPopupQuestion,
  removePopupQuestion,
}) {
  const agentsList = useAgents();
  const [selectedAgentsList, setSelectedAgentsList] = useState([]);
  const [selectedOption, setSelectedOption] = useState(
    dashboardPatientFFOptions,
  );
  const [dates, setDates] = useState(dashboardPatientFFDates);
  const [toggle, setToggle] = useState(dashboardPatientFFToggle);
  const [glassesModule, setGlassesModule] = useState(
    dashboardPatientGlassesModule,
  );
  const [contactLensesModule, setContactLensesModule] = useState(
    dashboardPatientContactLensesModule,
  );
  const [hearingAidsModule, setHearingAidsModule] = useState(
    dashboardPatientHearingAidsModule,
  );

  const frameSuppliersList = useRecordSuppliersAndBrands(
    glassesModule.vistaSol,
    0,
  );
  const [selectedFrameSuppliersList, setSelectedFrameSuppliersList] = useState(
    [],
  );

  const frameSupplierIds = useMemo(() => {
    return frameSuppliersList
      .filter((e) => selectedFrameSuppliersList.includes(e.nombre))
      .map((e) => e.id);
  }, [frameSuppliersList, selectedFrameSuppliersList]);

  const [frameBrandsList, setFrameBrandsList] = useState([]);
  const [selectedFrameBrandsList, setSelectedFrameBrandsList] = useState([]);

  const lensesSuppliersList = useRecordSuppliersAndBrands(
    glassesModule.vistaSol,
    1,
  );
  const [selectedLensesSuppliersList, setSelectedLensesSuppliersList] =
    useState([]);

  const lensesSupplierIds = useMemo(() => {
    return lensesSuppliersList
      .filter((e) => selectedLensesSuppliersList.includes(e.nombre))
      .map((e) => e.id);
  }, [lensesSuppliersList, selectedLensesSuppliersList]);

  const [lensesBrandsList, setLensesBrandsList] = useState([]);
  const [selectedLensesBrandsList, setSelectedLensesBrandsList] = useState([]);
  const glassesTypesList = useGlassesTypes();
  const [selectedGlassesTypesList, setSelectedGlassesTypesList] = useState([]);
  const [selectedGlassesTypesIdList, setSelectedGlassesTypesIdList] = useState(
    [],
  );

  const CLSuppliersList = useRecordSuppliersAndBrands(3);
  const [selectedCLSuppliersList, setSelectedCLSuppliersList] = useState([]);

  const CLSupplierIds = useMemo(() => {
    return CLSuppliersList.filter((e) =>
      selectedCLSuppliersList.includes(e.nombre),
    ).map((e) => e.id);
  }, [CLSuppliersList, selectedCLSuppliersList]);

  const [CLBrandsList, setCLBrandsList] = useState([]);
  const [selectedCLBrandsList, setSelectedCLBrandsList] = useState([]);

  const CLTypesList = useCLTypes();
  const [selectedCLTypesList, setSelectedCLTypesList] = useState([]);
  const [selectedCLUsesList, setSelectedCLUsesList] = useState([]);

  const HASuppliersList = useRecordSuppliersAndBrands(4);
  const [selectedHASuppliersList, setSelectedHASuppliersList] = useState([]);

  const HASupplierIds = useMemo(() => {
    return HASuppliersList.filter((e) =>
      selectedHASuppliersList.includes(e.nombre),
    ).map((e) => e.id);
  }, [HASuppliersList, selectedHASuppliersList]);

  const [HABrandsList, setHABrandsList] = useState([]);
  const [selectedHABrandsList, setSelectedHABrandsList] = useState([]);
  const [selectedHATypesList, setSelectedHATypesList] = useState([]);

  function getSelectedIndex() {
    const selectedKey = Object.keys(selectedOption).find(
      (key) => selectedOption[key],
    );

    if (!selectedKey) return 0;

    return Object.keys(selectedOption).indexOf(selectedKey) + 1;
  }

  function handleActiveOption(key) {
    setSelectedOption((prev) =>
      Object.keys(prev).reduce((acc, curr) => {
        acc[curr] = curr === key ? !prev[key] : false;
        return acc;
      }, {}),
    );
  }

  function resetGlassesFilters() {
    setGlassesModule(dashboardPatientGlassesModule);
    setSelectedFrameSuppliersList([]);
    setSelectedFrameBrandsList([]);
    setSelectedLensesSuppliersList([]);
    setSelectedLensesBrandsList([]);
    setSelectedGlassesTypesList([]);
  }

  function resetCLFilters() {
    setContactLensesModule(dashboardPatientContactLensesModule);
    setSelectedCLSuppliersList([]);
    setSelectedCLBrandsList([]);
    setSelectedCLTypesList([]);
    setSelectedCLUsesList([]);
  }

  function resetHAFilters() {
    setHearingAidsModule(dashboardPatientHearingAidsModule);
    setSelectedHASuppliersList([]);
    setSelectedHABrandsList([]);
    setSelectedHATypesList([]);
  }

  function resetFilters() {
    setDates(dashboardPatientFFDates);
    setToggle(dashboardPatientFFToggle);
    setSelectedAgentsList([]);
    resetGlassesFilters();
    resetCLFilters();
    resetHAFilters();
  }

  useEffect(() => {
    setSelectedFrameSuppliersList([]);
    setSelectedLensesSuppliersList([]);
  }, [glassesModule.vistaSol]);

  useEffect(() => {
    const brands = frameSuppliersList
      .filter((e) => selectedFrameSuppliersList.includes(e.nombre))
      .flatMap((e) => e.marcas);

    setFrameBrandsList([...new Set(brands)].sort((a, b) => a.localeCompare(b)));
  }, [selectedFrameSuppliersList]);

  useEffect(() => {
    const brands = lensesSuppliersList
      .filter((e) => selectedLensesSuppliersList.includes(e.nombre))
      .flatMap((e) => e.marcas);

    setLensesBrandsList(
      [...new Set(brands)].sort((a, b) => a.localeCompare(b)),
    );
  }, [selectedLensesSuppliersList]);

  useEffect(() => {
    const brands = CLSuppliersList.filter((e) =>
      selectedCLSuppliersList.includes(e.nombre),
    ).flatMap((e) => e.marcas);
    setCLBrandsList([...new Set(brands)].sort((a, b) => a.localeCompare(b)));
  }, [selectedCLSuppliersList]);

  useEffect(() => {
    const brands = HASuppliersList.filter((e) =>
      selectedHASuppliersList.includes(e.nombre),
    ).flatMap((e) => e.marcas);
    setHABrandsList([...new Set(brands)].sort((a, b) => a.localeCompare(b)));
  }, [selectedHASuppliersList]);

  useEffect(() => {
    setSelectedFrameBrandsList(
      selectedFrameBrandsList.filter((e) => frameBrandsList.includes(e)),
    );
  }, [frameBrandsList]);

  useEffect(() => {
    setSelectedLensesBrandsList(
      selectedLensesBrandsList.filter((e) => lensesBrandsList.includes(e)),
    );
  }, [lensesBrandsList]);

  useEffect(() => {
    setSelectedCLBrandsList(
      selectedCLBrandsList.filter((e) => CLBrandsList.includes(e)),
    );
  }, [CLBrandsList]);

  useEffect(() => {
    setSelectedHABrandsList(
      selectedHABrandsList.filter((e) => HABrandsList.includes(e)),
    );
  }, [HABrandsList]);

  useEffect(() => {
    setGlassesModule((prev) => ({
      ...prev,
      proveedoresMontura: frameSupplierIds,
      marcasMonturas: selectedFrameBrandsList,
      proveedoresLentes: lensesSupplierIds,
      marcasLentes: selectedLensesBrandsList,
      tipoGafa: selectedGlassesTypesIdList,
    }));
  }, [
    frameSupplierIds,
    selectedFrameBrandsList,
    lensesSupplierIds,
    selectedLensesBrandsList,
    selectedGlassesTypesIdList,
  ]);

  useEffect(() => {
    setContactLensesModule((prev) => ({
      ...prev,
      proveedores: CLSupplierIds,
      marcas: selectedCLBrandsList,
      tipoLC: selectedCLTypesList,
      uso: selectedCLUsesList,
    }));
  }, [
    CLSupplierIds,
    selectedCLBrandsList,
    selectedCLTypesList,
    selectedCLUsesList,
  ]);

  useEffect(() => {
    setHearingAidsModule((prev) => ({
      ...prev,
      proveedores: HASupplierIds,
      marcas: selectedHABrandsList,
      tipoAudifono: selectedHATypesList,
    }));
  }, [HASupplierIds, selectedHABrandsList, selectedHATypesList]);

  useEffect(() => {
    setSelectedGlassesTypesIdList(
      glassesTypesList
        .filter((e) => selectedGlassesTypesList.includes(e.text))
        .map((e) => e.id),
    );
  }, [selectedGlassesTypesList]);

  useEffect(() => {
    onChange({
      tipoFicha: getSelectedIndex(),
      fechaCreacionDesde: dates.creationDateFrom,
      fechaCreacionHasta: dates.creationDateTo,
      seaPrimeraCreacion: toggle.firstCreated,
      seaUltimaCreacion: toggle.lastCreated,
      fechaRevisionDesde: dates.revisionDateFrom,
      fechaRevisionHasta: dates.revisionDateTo,
      seaPrimeraRevision: toggle.firstRevised,
      seaUltimaRevision: toggle.lastRevised,
      agentes: agentsList
        .filter((e) => selectedAgentsList.includes(e.text))
        .map((e) => e.id),
      filtrosGafas: glassesModule,
      filtrosLentesContacto: contactLensesModule,
      filtrosAudifonos: hearingAidsModule,
    });
  }, [
    selectedOption,
    dates,
    toggle,
    selectedAgentsList,
    glassesModule,
    contactLensesModule,
    hearingAidsModule,
  ]);

  return (
    <div className="dashboard-section">
      <div className="dashboard-section-title">
        <div
          className="opt-table-title opt-table-title-dash-stats"
          style={{ justifyContent: "flex-start" }}
        >
          Filtros de ficha
          {status !== null && (
            <button className="cell-icon" onClick={onClick}>
              <img
                src={
                  status
                    ? "/images/arrow-up-blue.svg"
                    : "/images/arrow-down-blue.svg"
                }
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            </button>
          )}
        </div>
        <button
          className="cell-label"
          tabIndex={-1}
          style={{ width: "120px" }}
          onClick={resetFilters}
        >
          Limpiar filtros
        </button>
      </div>
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <ButtonIconH
            text="Optometría"
            selected={selectedOption.optometria}
            onClick={() => {
              handleActiveOption("optometria");
            }}
          ></ButtonIconH>
          <ButtonIconH
            text="Gafas"
            selected={selectedOption.gafas}
            onClick={() => {
              handleActiveOption("gafas");
            }}
          ></ButtonIconH>
          <ButtonIconH
            text="Lentes de contacto"
            selected={selectedOption.lc}
            onClick={() => {
              handleActiveOption("lc");
            }}
          ></ButtonIconH>
          <ButtonIconH
            text="Audiometría"
            selected={selectedOption.audiometria}
            onClick={() => {
              handleActiveOption("audiometria");
            }}
          ></ButtonIconH>
          <ButtonIconH
            text="Audífonos"
            selected={selectedOption.audifonos}
            onClick={() => {
              handleActiveOption("audifonos");
            }}
          ></ButtonIconH>
        </div>
        <div className="dashboard-sec-filters">
          <div className="store-col-sec" style={{ width: "60%" }}>
            <div
              className="opt-table-content grid-11"
              id="patient-file-filters"
            >
              <div className="cell cell-start-content combined-cells-3">
                Fecha creación desde
              </div>
              <div className="cell cell-start-content combined-cells-2">
                <InputData
                  type="date"
                  max="9999-12-31"
                  size={0}
                  tabIndex={48}
                  input={dates.creationDateFrom}
                  onChange={(e) => {
                    setDates((prev) => ({
                      ...prev,
                      creationDateFrom: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-start-content combined-cells-3">
                Fecha revisión desde
              </div>
              <div className="cell cell-start-content combined-cells-2">
                <InputData
                  type="date"
                  max="9999-12-31"
                  size={0}
                  tabIndex={50}
                  input={dates.revisionDateFrom}
                  onChange={(e) => {
                    setDates((prev) => ({
                      ...prev,
                      revisionDateFrom: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-start-content combined-cells-3">
                Fecha creación hasta
              </div>
              <div className="cell cell-start-content combined-cells-2">
                <InputData
                  type="date"
                  max="9999-12-31"
                  size={0}
                  tabIndex={49}
                  input={dates.creationDateTo}
                  onChange={(e) => {
                    setDates((prev) => ({
                      ...prev,
                      creationDateTo: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-start-content combined-cells-3">
                Fecha revisión hasta
              </div>
              <div className="cell cell-start-content combined-cells-2">
                <InputData
                  type="date"
                  max="9999-12-31"
                  size={0}
                  tabIndex={51}
                  input={dates.revisionDateTo}
                  onChange={(e) => {
                    setDates((prev) => ({
                      ...prev,
                      revisionDateTo: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-start-content">Sea la primera</div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={52}
                  active={toggle.firstCreated}
                  onClick={() => {
                    setToggle((prev) => ({
                      ...prev,
                      firstCreated: !prev.firstCreated,
                      lastCreated: !prev.firstCreated
                        ? false
                        : prev.lastCreated,
                    }));
                  }}
                />
              </div>
              <div className="cell"></div>
              <div className="cell cell-start-content">Sea la última</div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={53}
                  active={toggle.lastCreated}
                  onClick={() => {
                    setToggle((prev) => ({
                      ...prev,
                      firstCreated: !prev.lastCreated
                        ? false
                        : prev.firstCreated,
                      lastCreated: !prev.lastCreated,
                    }));
                  }}
                ></CheckBox>
              </div>
              <div className="cell"></div>
              <div className="cell cell-start-content">Sea la primera</div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={54}
                  active={toggle.firstRevised}
                  onClick={() => {
                    setToggle((prev) => ({
                      ...prev,
                      firstRevised: !prev.firstRevised,
                      lastRevised: !prev.firstRevised
                        ? false
                        : prev.lastRevised,
                    }));
                  }}
                ></CheckBox>
              </div>
              <div className="cell"></div>
              <div className="cell cell-start-content">Sea la última</div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={55}
                  active={toggle.lastRevised}
                  onClick={() => {
                    setToggle((prev) => ({
                      ...prev,
                      firstRevised: !prev.lastRevised
                        ? false
                        : prev.firstRevised,
                      lastRevised: !prev.lastRevised,
                    }));
                  }}
                ></CheckBox>
              </div>
            </div>
          </div>
          <div className="store-col-sec" style={{ width: "40%" }}>
            <div className="data-field">
              <span className="label">Agente</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={56}
                type={"type-alphanum-ext"}
                options={agentsList.map((e) => e.text)}
                selectedOptions={selectedAgentsList}
                onChange={(e) => {
                  setSelectedAgentsList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedAgentsList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Agentes seleccionados: {selectedAgentsList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedAgentsList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedAgentsList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
          </div>
        </div>
        {/* Filtros gafas */}
        {selectedOption.gafas && (
          <div
            className="dashboard-subsection-content"
            style={{ flexDirection: "column" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <button
                className="cell-label"
                tabIndex={-1}
                style={{ width: "120px" }}
                onClick={resetGlassesFilters}
              >
                Limpiar filtros
              </button>
            </div>
            <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
              <div
                className="opt-table-content grid-2"
                id="glasses-type"
                style={{ marginTop: 30 }}
              >
                <div className="cell cell-start-content">Vista</div>
                <div className="cell">
                  <RadioButton
                    tabIndex={57}
                    active={glassesModule.vistaSol === 1}
                    onClick={() => {
                      setGlassesModule((prev) => ({
                        ...prev,
                        vistaSol: 1,
                      }));
                    }}
                  ></RadioButton>
                </div>
                <div className="cell cell-start-content">Sol</div>
                <div className="cell">
                  <RadioButton
                    tabIndex={58}
                    active={glassesModule.vistaSol === 2}
                    onClick={() => {
                      setGlassesModule((prev) => ({
                        ...prev,
                        vistaSol: 2,
                      }));
                    }}
                  ></RadioButton>
                </div>
                <div className="cell cell-start-content">Vista y sol</div>
                <div className="cell">
                  <RadioButton
                    tabIndex={59}
                    active={glassesModule.vistaSol === 0}
                    onClick={() => {
                      setGlassesModule((prev) => ({
                        ...prev,
                        vistaSol: 0,
                      }));
                    }}
                  ></RadioButton>
                </div>
              </div>
              <div className="dashboard-sec-filters" style={{ flex: 1 }}>
                <div className="store-col-sec" style={{ width: "50%" }}>
                  <div className="opt-table-subtitle">Datos de la montura</div>
                  <div className="data-field">
                    <span className="label">Proveedores</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={60}
                      type={"type-alphanum-ext"}
                      options={frameSuppliersList.map((e) => e.nombre)}
                      selectedOptions={selectedFrameSuppliersList}
                      onChange={(e) => {
                        setSelectedFrameSuppliersList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedFrameSuppliersList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Proveedores seleccionados:{" "}
                      {selectedFrameSuppliersList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedFrameSuppliersList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedFrameSuppliersList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                  <div className="data-field">
                    <span className="label">Marcas</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={61}
                      inactive={selectedFrameSuppliersList.length === 0}
                      type={"type-alphanum-ext"}
                      options={frameBrandsList}
                      selectedOptions={selectedFrameBrandsList}
                      onChange={(e) => {
                        setSelectedFrameBrandsList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedFrameBrandsList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Marcas seleccionadas: {selectedFrameBrandsList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedFrameBrandsList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedFrameBrandsList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                </div>
                <div className="store-col-sec" style={{ width: "50%" }}>
                  <div className="opt-table-subtitle">Datos de la lente</div>
                  <div className="data-field">
                    <span className="label">Proveedores</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={62}
                      type={"type-alphanum-ext"}
                      options={lensesSuppliersList.map((e) => e.nombre)}
                      selectedOptions={selectedLensesSuppliersList}
                      onChange={(e) => {
                        setSelectedLensesSuppliersList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedLensesSuppliersList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Proveedores seleccionados:{" "}
                      {selectedLensesSuppliersList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedLensesSuppliersList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedLensesSuppliersList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                  <div className="data-field">
                    <span className="label">Marcas</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={63}
                      inactive={selectedLensesSuppliersList.length === 0}
                      type={"type-alphanum-ext"}
                      options={lensesBrandsList}
                      selectedOptions={selectedLensesBrandsList}
                      onChange={(e) => {
                        setSelectedLensesBrandsList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedLensesBrandsList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Marcas seleccionadas: {selectedLensesBrandsList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedLensesBrandsList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedLensesBrandsList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div
                className="store-col-sec"
                style={{ marginTop: 30, width: "40%" }}
              >
                <div className="data-field">
                  <span className="label">Tipo gafa</span>
                  <InputIconSearchPanelMultiSelection
                    size={0}
                    tabIndex={64}
                    type={"type-alphanum-ext"}
                    options={glassesTypesList.map((e) => e.text)}
                    selectedOptions={selectedGlassesTypesList}
                    onChange={(e) => {
                      setSelectedGlassesTypesList(e);
                    }}
                  ></InputIconSearchPanelMultiSelection>
                </div>
                {selectedGlassesTypesList.length > 0 && (
                  <span style={{ fontWeight: "500", fontSize: 13 }}>
                    Tipos seleccionados: {selectedGlassesTypesList.length}
                  </span>
                )}
                <div className="tag-content">
                  {selectedGlassesTypesList.map((e, i) => {
                    return (
                      <Tag
                        text={e}
                        key={i}
                        tagId={i}
                        onClick={(e) => {
                          setSelectedGlassesTypesList((prev) =>
                            prev.filter((element) => element !== e),
                          );
                        }}
                      ></Tag>
                    );
                  })}
                </div>
                <div
                  className="opt-table-content grid-8"
                  id="dashboard-type-file-grid"
                >
                  <div className="cell cell-start-content">Cil desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={65}
                      input={glassesModule.cilDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          cilDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Esf desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={67}
                      input={glassesModule.esfDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          esfDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Add desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={69}
                      input={glassesModule.addDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          addDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell cell-start-content">Cil hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={66}
                      input={glassesModule.cilHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          cilHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Esf hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={68}
                      input={glassesModule.esfHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          esfHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Add hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={70}
                      input={glassesModule.addHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setGlassesModule((prev) => ({
                          ...prev,
                          addHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Filtros LC */}
        {selectedOption.lc && (
          <div
            className="dashboard-subsection-content"
            style={{ flexDirection: "column" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                marginBottom: 20,
              }}
            >
              <button
                className="cell-label"
                tabIndex={-1}
                style={{ width: "120px" }}
                onClick={resetCLFilters}
              >
                Limpiar filtros
              </button>
            </div>
            <div
              style={{
                display: "flex",
                gap: 40,
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <div className="dashboard-sec-filters" style={{ flex: 1 }}>
                <div className="store-col-sec" style={{ width: "50%" }}>
                  <div className="data-field">
                    <span className="label">Proveedores</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={71}
                      type={"type-alphanum-ext"}
                      options={CLSuppliersList.map((e) => e.nombre)}
                      selectedOptions={selectedCLSuppliersList}
                      onChange={(e) => {
                        setSelectedCLSuppliersList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedCLSuppliersList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Proveedores seleccionados:{" "}
                      {selectedCLSuppliersList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedCLSuppliersList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedCLSuppliersList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                  <div className="data-field">
                    <span className="label">Marcas</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={72}
                      type={"type-alphanum-ext"}
                      inactive={selectedCLSuppliersList.length === 0}
                      options={CLBrandsList}
                      selectedOptions={selectedCLBrandsList}
                      onChange={(e) => {
                        setSelectedCLBrandsList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedCLBrandsList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Marcas seleccionadas: {selectedCLBrandsList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedCLBrandsList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedCLBrandsList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                </div>
                <div className="store-col-sec" style={{ width: "50%" }}>
                  <div className="data-field">
                    <span className="label">Tipo LC</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={73}
                      type={"type-alphanum-ext"}
                      options={CLTypesList.map((e) => e.text)}
                      selectedOptions={selectedCLTypesList}
                      onChange={(e) => {
                        setSelectedCLTypesList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedCLTypesList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Tipos seleccionados: {selectedCLTypesList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedCLTypesList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedCLTypesList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                  <div className="data-field">
                    <span className="label">Uso</span>
                    <InputIconSearchPanelMultiSelection
                      size={0}
                      tabIndex={74}
                      type={"type-alphanum-ext"}
                      options={contactLensesUseOptions
                        .filter((e) => e.id !== "")
                        .map((e) => e.text)}
                      selectedOptions={selectedCLUsesList}
                      onChange={(e) => {
                        setSelectedCLUsesList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedCLUsesList.length > 0 && (
                    <span style={{ fontWeight: "500", fontSize: 13 }}>
                      Usos seleccionados: {selectedCLUsesList.length}
                    </span>
                  )}
                  <div className="tag-content">
                    {selectedCLUsesList.map((e, i) => {
                      return (
                        <Tag
                          text={e}
                          key={i}
                          tagId={i}
                          onClick={(e) => {
                            setSelectedCLUsesList((prev) =>
                              prev.filter((element) => element !== e),
                            );
                          }}
                        ></Tag>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="store-col-sec">
                <div
                  className="opt-table-content grid-8"
                  id="dashboard-type-file-grid"
                >
                  <div className="cell cell-start-content">Cil desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={75}
                      input={contactLensesModule.cilDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          cilDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Esf desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={77}
                      input={contactLensesModule.esfDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          esfDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Add desde</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={79}
                      input={contactLensesModule.addDesde}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          addDesde: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell cell-start-content">Cil hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={76}
                      input={contactLensesModule.cilHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          cilHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Esf hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={78}
                      input={contactLensesModule.esfHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          esfHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-start-content">Add hasta</div>
                  <div className="cell">
                    <InputDataControl
                      type="type-optofloat"
                      size={0}
                      tabIndex={80}
                      input={contactLensesModule.addHasta}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        setContactLensesModule((prev) => ({
                          ...prev,
                          addHasta: e,
                        }));
                      }}
                    ></InputDataControl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Filtros audifonos */}
        {selectedOption.audifonos && (
          <div
            className="dashboard-subsection-content"
            style={{ flexDirection: "column" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                marginBottom: 10,
              }}
            >
              <button
                className="cell-label"
                tabIndex={-1}
                style={{ width: "120px" }}
                onClick={resetHAFilters}
              >
                Limpiar filtros
              </button>
            </div>
            <div
              style={{
                display: "flex",
                gap: 40,
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <div className="store-col-sec" style={{ flex: 1 }}>
                <div className="data-field">
                  <span className="label">Proveedores</span>
                  <InputIconSearchPanelMultiSelection
                    size={0}
                    tabIndex={81}
                    type={"type-alphanum-ext"}
                    options={HASuppliersList.map((e) => e.nombre)}
                    selectedOptions={selectedHASuppliersList}
                    onChange={(e) => {
                      setSelectedHASuppliersList(e);
                    }}
                  ></InputIconSearchPanelMultiSelection>
                </div>
                {selectedHASuppliersList.length > 0 && (
                  <span style={{ fontWeight: "500", fontSize: 13 }}>
                    Proveedores seleccionados: {selectedHASuppliersList.length}
                  </span>
                )}
                <div className="tag-content">
                  {selectedHASuppliersList.map((e, i) => {
                    return (
                      <Tag
                        text={e}
                        key={i}
                        tagId={i}
                        onClick={(e) => {
                          setSelectedHASuppliersList((prev) =>
                            prev.filter((element) => element !== e),
                          );
                        }}
                      ></Tag>
                    );
                  })}
                </div>
              </div>
              <div className="store-col-sec" style={{ flex: 1 }}>
                <div className="data-field">
                  <span className="label">Marcas</span>
                  <InputIconSearchPanelMultiSelection
                    size={0}
                    tabIndex={82}
                    type={"type-alphanum-ext"}
                    inactive={selectedHASuppliersList.length === 0}
                    options={HABrandsList}
                    selectedOptions={selectedHABrandsList}
                    onChange={(e) => {
                      setSelectedHABrandsList(e);
                    }}
                  ></InputIconSearchPanelMultiSelection>
                </div>
                {selectedHABrandsList.length > 0 && (
                  <span style={{ fontWeight: "500", fontSize: 13 }}>
                    Marcas seleccionadas: {selectedHABrandsList.length}
                  </span>
                )}
                <div className="tag-content">
                  {selectedHABrandsList.map((e, i) => {
                    return (
                      <Tag
                        text={e}
                        key={i}
                        tagId={i}
                        onClick={(e) => {
                          setSelectedHABrandsList((prev) =>
                            prev.filter((element) => element !== e),
                          );
                        }}
                      ></Tag>
                    );
                  })}
                </div>
              </div>
              <div className="store-col-sec" style={{ flex: 1 }}>
                <div className="data-field">
                  <span className="label">Tipo audifono</span>
                  <InputIconSearchPanelMultiSelection
                    size={0}
                    tabIndex={83}
                    type={"type-alphanum-ext"}
                    options={hearingAidsTypeOptions
                      .filter((e) => e.id !== "")
                      .map((e) => e.text)}
                    selectedOptions={selectedHATypesList}
                    onChange={(e) => {
                      setSelectedHATypesList(e);
                    }}
                  ></InputIconSearchPanelMultiSelection>
                </div>
                {selectedHATypesList.length > 0 && (
                  <span style={{ fontWeight: "500", fontSize: 13 }}>
                    Tipos seleccionados: {selectedHATypesList.length}
                  </span>
                )}
                <div className="tag-content">
                  {selectedHATypesList.map((e, i) => {
                    return (
                      <Tag
                        text={e}
                        key={i}
                        tagId={i}
                        onClick={(e) => {
                          setSelectedHATypesList((prev) =>
                            prev.filter((element) => element !== e),
                          );
                        }}
                      ></Tag>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
